<script setup lang="ts">
import { computed, inject, Ref, ref } from 'vue';
import { type Swimlane, type OpportunityBase, type OpportunityType } from '@apparatix/types/Leads';
import { useStringFunctions } from '@apparatix/composables';
import { useOpportunityStore } from '@apparatix/stores/useOpportunityStore';

type LaneProps = { type: OpportunityType, opportunityCount: number } & Omit<Swimlane, 'value'>;

const props = defineProps<LaneProps>();

const { formatAsCurrency, isEmptyString } = useStringFunctions();
const store = useOpportunityStore();

const [width] = inject<[Ref<string>, Ref<string>]>('swimlane-sizes');

const noOpportunitiesMessage = computed(() => {
  if (store.areOpportunitiesHiddenByFilter(props.type, props.id)) {
    return `The ${props.type === 'Opportunity' ? 'opportunities' : 'leads'} in this lane are hidden by your current filters`;
  }

  return `There are no ${props.type === 'Opportunity' ? 'opportunities' : 'leads'} in this lane`;
});

const laneValue = computed(() => store.opportunities[props.type].filter(opportunity => opportunity.swimlaneId === props.id)?.reduce((acc, current) => acc + current.value, 0));

const shouldRender = computed(() => !isEmptyString(width.value));

const el = ref<HTMLDivElement>();

const emit = defineEmits<{
  (e: 'update:change-lane', newLane: number, oldLane: number, leadId: number): void;
}>();

const onDrop = (e: DragEvent) => {
  e.preventDefault();
  const lead: OpportunityBase = JSON.parse(e.dataTransfer.getData('text/javascript'));
  emit('update:change-lane', props.id, lead.swimlaneId, lead.id);
};

const onDragEnter = (e: DragEvent) => e.preventDefault();

const onDragOver = (e: DragEvent) => e.preventDefault();

const getHeaderLabel = () => {
  if (props.type === 'Opportunity') {
    return props.opportunityCount === 1 ? 'opportunity' : 'opportunities';
  }

  return props.opportunityCount === 1 ? 'lead' : 'leads';
};

</script>
<template>
  <div
    v-if="shouldRender"
    ref="el"
    class="h-full shrink-0 flex flex-col gap-1 p-3 bg-[var(--neutral-background)] rounded-lg"
    :style="{width}"
    @drop="onDrop"
    @dragenter="onDragEnter"
    @dragover="onDragOver"
  >
    <div class="flex flex-col p-2">
      <h3 class="font-medium">
        {{ status }}
      </h3>
      <p v-if="props.status === 'Completed'" class="text-sm text-[var(--swimlane-secondary-text)]">
        {{ props.opportunityCount }} {{ getHeaderLabel() }}
      </p>
      <p v-else class="text-sm text-[var(--swimlane-secondary-text)]">
        {{ formatAsCurrency(laneValue, false) }} &ndash; {{ props.opportunityCount }} {{ getHeaderLabel() }}
      </p>
    </div>
    <slot v-if="opportunityCount > 0" />
    <p v-else class="text-sm text-[var(--swimlane-secondary-text)]">
      {{ noOpportunitiesMessage }}
    </p>
  </div>
</template>
<style scoped>
.swimlane-lane {
  flex-shrink: 0;
}
</style>
